import {
	type CollectibleAuctionFront,
	type CollectibleAuctionListRefined,
	type CollectibleEventListRefined
} from '@capsicum/common';
import { useMainCurrenciesQuery } from '@capsicum/socios-core';
import { useMemo } from 'react';

//useCollectibleEventsQueries
import { useCollectibleAuctionsTeamByPartnerIdFrontQueries } from '../data-access/queries/useCollectibleEventsQueries';
import { auctionsUtils, parseAuctionFront } from '../util/auctionsUtils';

export type CollectibleEventListRefinedExtended = CollectibleEventListRefined & {
	id: string;
	showSeeAllButton?: boolean;
};

export const useAuctionsByPartnerId = (options?: { auctionsPerEvent?: number; partnerId: string }) => {
	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};
	const { getAwayTeam, getHomeTeam } = auctionsUtils();

	const params = {
		altCurrencyId: altCurrency?.id,
		auctionsPerEvent: options?.auctionsPerEvent,
		mainCurrencyId: defaultCurrency?.id,
		partnerId: options?.partnerId || ''
	};

	const {
		data,
		isFetching,
		isLoading,
		refetch: refetchEvents
	} = useCollectibleAuctionsTeamByPartnerIdFrontQueries(params);

	const event: CollectibleEventListRefinedExtended | undefined = useMemo(() => {
		const eventsMap = new Map();

		if (data) {
			const allAuctions = data?.data as Array<CollectibleAuctionFront>;

			allAuctions.forEach(auction => {
				if (auction.event) {
					parseAuctionFront(auction);

					if (eventsMap.has(auction.event.id)) {
						eventsMap.get(auction.event.id).push(auction);
					} else {
						eventsMap.set(auction.event.id, [auction]);
					}
				}
			});

			return [...eventsMap].map(([eventId, auctions]) => {
				return {
					auctions,
					id: eventId,
					showSeeAllButton: auctions.length > 5,
					teamEvents: auctions[0]?.event?.teamsQualifier
				};
			})[0];
		} else {
			return undefined;
		}
	}, [data]);

	const isEventClosed = (auctions: Array<CollectibleAuctionListRefined>) => {
		return !auctions.filter(auction => auction?.status !== 'finished').length;
	};

	return {
		event,
		getAwayTeam,
		getHomeTeam,
		isEventClosed,
		isFetching,
		isLoading,
		refetchEvents
	};
};
